import { useImmer } from "use-immer";
import { useEffect, useState, useRef } from "react";
import moment from "moment/moment";

import ApiConnector from "../connector/ApiConnector";
import AuditlogFilter from "../component/auditlogPage/AuditlogFilter";
import AuditlogColumnConfigurator from "../component/auditlogPage/AuditlogColumnConfigurator";
import AuditlogTable from "../component/auditlogPage/AuditlogTable";
import GetFileViaS3 from "../component/S3FileAnchor";

import {
  faAnglesLeft,
  faAnglesRight,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const FILTER_TEMPLATE = {
  createdMin: moment().subtract(7, "days").format("YYYY-MM-DDTHH:mm:ss"),
  createdMax: moment().format("YYYY-MM-DDTHH:mm:ss"),
  excludeUserIds: ["PABLO"],
  userId: null,
  eventTypes: [],
  pageSize: 30,
  pageNumber: 0,
};

const AuditlogPage = () => {
  // State
  const [filterConfig, updateFilterConfig] = useImmer(FILTER_TEMPLATE);
  const [data, updateData] = useImmer([]);
  const [totalCount, setTotalCount] = useState(0);
  const [displayAttributes, setDisplayAttributes] = useState([
    "eventType",
    "created",
    "userId",
    "newValue",
    "oldValue",
    "actions",
  ]);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isLoadInProgress = useRef(false);

  // Apply full-width layout for this page
  useEffect(() => {
    document.body.classList.add("full-width-page");
    return () => {
      document.body.classList.remove("full-width-page");
    };
  }, []);

  // Derived state
  const effectivePageSize = filterConfig.pageSize || 30;
  const startIndex = filterConfig.pageNumber * effectivePageSize;
  const endIndex = startIndex + data.length;
  const canGoPrev = filterConfig.pageNumber > 0;
  const canGoNext = endIndex < totalCount;
  const totalPages = Math.ceil(totalCount / effectivePageSize);

  // Build query params
  const buildQueryParams = () => ({
    createdMin: filterConfig.createdMin
      ? filterConfig.createdMin + "Z"
      : null,
    createdMax: filterConfig.createdMax
      ? filterConfig.createdMax + "Z"
      : null,
    userId: filterConfig.userId || null,
    excludeUserIds: filterConfig.excludeUserIds || null,
    eventTypes:
      filterConfig.eventTypes && filterConfig.eventTypes.length > 0
        ? filterConfig.eventTypes
        : null,
    pageSize: effectivePageSize,
    offset: filterConfig.pageNumber * effectivePageSize,
    displayCount: true,
  });

  // Data loading
  const loadData = async () => {
    if (isLoadInProgress.current) return;
    isLoadInProgress.current = true;
    setIsLoading(true);

    try {
      const params = buildQueryParams();
      const response = await ApiConnector.getAuditlogs(params);
      updateData(response.data || []);
      setTotalCount(response.count || 0);
      setLastRefreshed(moment());
      setError(null);
    } catch (err) {
      console.error("Failed to load audit logs:", err);
      setError("Failed to load audit logs: " + err.message);
      updateData([]);
      setTotalCount(0);
    } finally {
      isLoadInProgress.current = false;
      setIsLoading(false);
    }
  };

  // Load on mount + whenever filters change
  useEffect(() => {
    loadData();
  }, [
    filterConfig.createdMin,
    filterConfig.createdMax,
    filterConfig.userId,
    filterConfig.excludeUserIds,
    filterConfig.eventTypes,
    filterConfig.pageSize,
    filterConfig.pageNumber,
  ]);

  // Navigation
  const setPageNumber = (uiValue) => {
    updateFilterConfig((draft) => {
      const totalPages = Math.ceil(totalCount / draft.pageSize);

      let internal = Number(uiValue) - 1; // UI = 1-based → internal = 0-based

      internal = Math.max(0, Math.min(totalPages - 1, internal));

      draft.pageNumber = internal;
    });
  };

  return (
    <>
      <header className="subheader header-with-save">
        <h1>Audit Logs</h1>
      </header>

      <main>
        <div style={{ maxWidth: "60rem", marginLeft: "auto", marginRight: "auto", marginTop: "1.5rem", borderTop: "none", borderLeft: "1px solid #e0e0e0", borderRight: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", borderRadius: "4px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "0.5rem 1rem 0.75rem", marginBottom: "1rem" }}>
        {/* Paging header */}
        <div className="row">
          <div className="col">
            <h2 style={{ marginTop: 0 }}>Audit Logs</h2>
          </div>

          <div className="col">
            <h2 className="float-right" style={{ marginTop: 0 }}>
              <FontAwesomeIcon
                icon={faAnglesLeft}
                onClick={() =>
                  canGoPrev && setPageNumber(filterConfig.pageNumber + 1 - 10)
                }
                className={canGoPrev ? "icon-clickable" : "icon-disabled"}
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAngleLeft}
                onClick={() =>
                  canGoPrev && setPageNumber(filterConfig.pageNumber + 1 - 1)
                }
                className={canGoPrev ? "icon-clickable" : "icon-disabled"}
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAngleRight}
                onClick={() =>
                  canGoNext && setPageNumber(filterConfig.pageNumber + 1 + 1)
                }
                className={canGoNext ? "icon-clickable" : "icon-disabled"}
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAnglesRight}
                onClick={() =>
                  canGoNext && setPageNumber(filterConfig.pageNumber + 1 + 10)
                }
                className={canGoNext ? "icon-clickable" : "icon-disabled"}
              />
              &nbsp;&nbsp;&nbsp;
            </h2>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div
            style={{
              background: "#fee",
              padding: "1em",
              marginBottom: "1em",
              border: "1px solid #fcc",
              borderRadius: "3px",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && <p>Loading audit logs...</p>}

        {/* Filtering parameters */}
        <details>
          <summary>Filtering parameters</summary>

          <AuditlogFilter
            filterConfig={filterConfig}
            onChange={(newConfig) => {
              updateFilterConfig(() => ({
                ...newConfig,
                pageNumber: 0, // Reset to first page
              }));
            }}
          />

          {/* Column configurator */}
          <details>
            <summary>Displayed columns</summary>
            <AuditlogColumnConfigurator onChange={setDisplayAttributes} />
          </details>

          {/* Export */}
          <GetFileViaS3
            caption="Export to XLSX"
            containerClassName=""
            disabled={false}
            filterKey={JSON.stringify({
              createdMin: filterConfig.createdMin,
              createdMax: filterConfig.createdMax,
              userId: filterConfig.userId,
              excludeUserIds: filterConfig.excludeUserIds,
              eventTypes: filterConfig.eventTypes,
            })}
            onFirstClick={() =>
              ApiConnector.generateAuditLogExcelToS3({
                createdMin: filterConfig.createdMin
                  ? filterConfig.createdMin + "Z"
                  : undefined,
                createdMax: filterConfig.createdMax
                  ? filterConfig.createdMax + "Z"
                  : undefined,
                userId: filterConfig.userId || undefined,
                excludeUserIds: filterConfig.excludeUserIds,
                eventTypes:
                  filterConfig.eventTypes && filterConfig.eventTypes.length > 0
                    ? filterConfig.eventTypes
                    : undefined,
              })
            }
            onForceClick={() =>
              ApiConnector.generateAuditLogExcelToS3({
                createdMin: filterConfig.createdMin
                  ? filterConfig.createdMin + "Z"
                  : undefined,
                createdMax: filterConfig.createdMax
                  ? filterConfig.createdMax + "Z"
                  : undefined,
                userId: filterConfig.userId || undefined,
                excludeUserIds: filterConfig.excludeUserIds,
                eventTypes:
                  filterConfig.eventTypes && filterConfig.eventTypes.length > 0
                    ? filterConfig.eventTypes
                    : undefined,
                forceRegenerate: true,
              })
            }
            onLoadFiles={() => ApiConnector.listExportFiles("audit_logs_")}
          />
        </details>

        </div>

        {/* Table — full width */}
        <AuditlogTable auditlogs={data} displayedAttributes={displayAttributes} />

        {/* Page info */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "1em",
          }}
        >
          <p>
            Page {filterConfig.pageNumber + 1} / {totalPages || 1} (
            {startIndex + 1}-{endIndex} of {totalCount})
          </p>
          <p>
            Last refreshed:{" "}
            {lastRefreshed ? lastRefreshed.format("YYYY-MM-DD HH:mm:ss") : "-"}
          </p>
        </div>
      </main>
    </>
  );
};

export default AuditlogPage;
